import React, { Fragment } from 'react';
import { Modal, Button } from 'semantic-ui-react';
import ReactTable from 'react-table';
import { connect } from 'react-redux';
import _ from 'lodash';
import qs from 'qs';

import { TitledCard } from 'Components/';
import { extractTruthyKeys } from 'Common/FormHelper';
import { setNotificationDialog } from 'Components/dialogs/state/actions';
import { NOTIFICATION_DIALOG_MESSAGES } from 'Common/Constants';
import { PAGE_REFS, QUERY_PARAMS } from 'Pages/manageapplication/helpers/hub';
import { ApplicationFieldEnum as ApplicationFields } from 'Common/FieldEnum';
import { createToast, toastType } from 'Common/ToastHelper';
import * as CommonStateSelectors from 'State/selectors';
import * as PageStateCacheActions from 'State/pagestatecache/actions';
import * as RIAStateCacheSelectors from 'State/pagestatecache/pages/reviewincomingapplications/selectors';
import { PAGE_STATE_CACHES } from 'State/pagestatecache/helpers/Constants';

import * as DuplicateAppsSelectors from '../state/duplicateapps/selectors';
import * as DuplicateAppsActions from '../state/duplicateapps/actions';
import {
  createHouseholdTableColumns,
  createInitialResultingApplication,
  createApplicationFromSelectedRows,
  isAlreadyOnApplication,
  canStartNewApplication,
  getCreateOrEditErrorMsg,
} from '../helpers';
import { CustomMemberFields } from '../helpers/Constants';
import { DuplicateApplicationsTable } from './DuplicateApplicationsTable';

const STEPS = {
  SelectExistingApplication: 'SelectExistingApplication',
  MergeDuplicateApplication: 'MergeDuplicateApplication',
};

const ActionTypes = {
  UpdateResultingApp: 'UpdateResultingApp',
  UpdateIncomingCheckedRowsMap: 'UpdateIncomingCheckedRowsMap',
  UpdateExistingCheckedRowsMap: 'UpdateExistingCheckedRowsMap',
  UpdateSelectedHHId: 'UpdateSelectedHHId',
  UpdateExpandedMap: 'UpdateExpandedMap',
  SetStep: 'SetStep',
};

function createInitialState({ existingApp, savedModalState }) {
  if (!_.isNil(savedModalState)) {
    return { ...savedModalState };
  }

  return {
    step: STEPS.SelectExistingApplication,
    existingAppCheckedRowsMap: {},
    selectedHHId: null,
    expanded: {},
    incomingAppCheckedRowsMap: {},
    resultingApp: createInitialResultingApplication(existingApp),
  };
}

function reducer(state, action) {
  const { type, payload } = action;

  switch (type) {
    case ActionTypes.SetStep:
      return { ...state, step: payload };
    case ActionTypes.UpdateResultingApp:
      return { ...state, resultingApp: payload };
    case ActionTypes.UpdateIncomingCheckedRowsMap:
      return {
        ...state,
        incomingAppCheckedRowsMap: {
          ...state.incomingAppCheckedRowsMap,
          ...payload,
        },
      };
    case ActionTypes.UpdateExistingCheckedRowsMap:
      return {
        ...state,
        existingAppCheckedRowsMap: {
          ...state.existingAppCheckedRowsMap,
          ...payload,
        },
      };
    case ActionTypes.UpdateSelectedHHId:
      return { ...state, selectedHHId: payload };
    case ActionTypes.UpdateExpandedMap:
      return { ...state, expanded: { ...state.expanded, ...payload } };
    default:
      return state;
  }
}

const ReviewDuplicatesModal = ({
  existingMembers,
  incomingMembers,
  existingApp,
  appComparisonLoading,
  updateLoading,
  setNotificationDialog,
  updateSuccessful,
  setUpdateSuccessful,
  errors,
  clearErrors,
  history,
  startNewApplication,
  duplicateStudentMatches,
  incomingHOH,
  getExistingAppComparison,
  scannedAppId,
  caseTypes,
  existingStudents,
  editApplication,
  getState,
  addPageState,
  savedModalState,
  removePageState,
  ...modalProps
}) => {
  const [state, dispatch] = React.useReducer(
    reducer,
    { existingApp, savedModalState },
    createInitialState
  );
  const {
    step,
    existingAppCheckedRowsMap,
    selectedHHId,
    expanded,
    incomingAppCheckedRowsMap,
    resultingApp,
  } = state;

  React.useEffect(
    function showUpdateMessage() {
      if (updateSuccessful) {
        createToast(
          toastType.success,
          'Success',
          'Updated Existing Application',
          true
        );
        setUpdateSuccessful(false);
      }
    },
    [updateSuccessful]
  );

  React.useEffect(
    function showAndClearErrors() {
      errors.forEach((error) => {
        createToast(toastType.error, 'Error', error.Message, true);
      });
      return function cleanup() {
        clearErrors();
      };
    },
    [errors]
  );

  React.useEffect(
    function initExistingAppCheckedRowsMap() {
      if (_.isNil(savedModalState)) {
        let checkedRowsMap = {};
        _.range(0, existingMembers.length).forEach((i) => {
          checkedRowsMap[i] = false;
        });

        dispatch({
          type: ActionTypes.UpdateExistingCheckedRowsMap,
          payload: checkedRowsMap,
        });
      }
    },
    [existingMembers]
  );

  const showAlreadyAddedError = () => {
    createToast(
      toastType.error,
      'Error',
      'Member has already been added to the Resulting Application.',
      true
    );
  };

  React.useEffect(
    function initIncomingAppCheckedRowsMap() {
      if (_.isNil(savedModalState)) {
        let checkedRowsMap = {};
        _.range(0, incomingMembers.length).forEach((i) => {
          checkedRowsMap[i] = false;
        });

        dispatch({
          type: ActionTypes.UpdateIncomingCheckedRowsMap,
          payload: checkedRowsMap,
        });
      }
    },
    [incomingMembers, selectedHHId]
  );

  React.useEffect(
    function initResultingApp() {
      if (_.isNil(savedModalState)) {
        dispatch({
          type: ActionTypes.UpdateResultingApp,
          payload: createInitialResultingApplication(existingApp),
        });
      }
    },
    [existingApp]
  );

  const selectedExistingMembers = React.useMemo(
    () =>
      extractTruthyKeys(existingAppCheckedRowsMap).map(
        (i) => existingMembers[i]
      ),
    [existingAppCheckedRowsMap]
  );

  React.useEffect(
    function updateResultingApp() {
      const selectedIncomingMembers = extractTruthyKeys(
        incomingAppCheckedRowsMap
      ).map((i) => incomingMembers[i]);

      dispatch({
        type: ActionTypes.UpdateResultingApp,
        payload: createApplicationFromSelectedRows(
          existingApp,
          selectedExistingMembers,
          selectedIncomingMembers,
          incomingHOH,
          existingStudents
        ),
      });
    },
    [existingAppCheckedRowsMap, incomingAppCheckedRowsMap]
  );

  const resultingAppMembers = React.useMemo(
    () =>
      resultingApp[ApplicationFields.Members].concat(
        resultingApp[ApplicationFields.Students]
      ),
    [resultingApp]
  );
  const disableStartNewApplicationBtn = !canStartNewApplication(
    existingMembers,
    resultingApp[ApplicationFields.Students],
    duplicateStudentMatches,
    selectedExistingMembers
  );
  const existingAppColumns = createHouseholdTableColumns(
    (index, checked) => {
      if (
        checked &&
        isAlreadyOnApplication({
          member: existingMembers[index],
          existingCheckedRows: existingAppCheckedRowsMap,
          incomingCheckedRows: incomingAppCheckedRowsMap,
          existingMembers,
          incomingMembers,
        })
      ) {
        showAlreadyAddedError();
        return;
      }

      dispatch({
        type: ActionTypes.UpdateExistingCheckedRowsMap,
        payload: { [index]: checked },
      });
    },
    existingAppCheckedRowsMap,
    caseTypes
  );
  const incomingAppColumns = createHouseholdTableColumns(
    (index, checked) => {
      if (
        checked &&
        isAlreadyOnApplication({
          member: incomingMembers[index],
          existingCheckedRows: existingAppCheckedRowsMap,
          incomingCheckedRows: incomingAppCheckedRowsMap,
          existingMembers,
          incomingMembers,
        })
      ) {
        showAlreadyAddedError();
        return;
      }

      dispatch({
        type: ActionTypes.UpdateIncomingCheckedRowsMap,
        payload: { [index]: checked },
      });
    },
    incomingAppCheckedRowsMap,
    caseTypes
  );
  const resultingAppColumns = React.useMemo(
    () => createHouseholdTableColumns(null, null, caseTypes),
    [caseTypes]
  );

  const savePageState = () => {
    const pageState = {
      ReviewAppGrid: getState(),
      ReviewDuplicatesModal: state,
    };

    addPageState(PAGE_STATE_CACHES.ReviewIncomingApps, pageState);
  };

  React.useEffect(function consumeSavedState() {
    if (!_.isNil(savedModalState)) {
      removePageState(PAGE_STATE_CACHES.ReviewIncomingApps);
    }
  }, []);

  return (
    <Modal {...modalProps} className="rvia-modal">
      <Modal.Header>Review Duplicate Applications</Modal.Header>
      <Modal.Content scrolling={true}>
        {step === STEPS.SelectExistingApplication ? (
          <DuplicateApplicationsTable
            setSelectedHHId={(selectedHHId) =>
              dispatch({
                type: ActionTypes.UpdateSelectedHHId,
                payload: selectedHHId,
              })
            }
            selectedHHId={selectedHHId}
            expanded={expanded}
            setExpanded={(expandedObj) =>
              dispatch({
                type: ActionTypes.UpdateExpandedMap,
                payload: expandedObj,
              })
            }
          />
        ) : (
          <Fragment>
            <TitledCard title="Existing Application">
              <ReactTable
                data={existingMembers}
                columns={existingAppColumns}
                minRows={5}
                pageSize={5}
                showPageJump={false}
                showPageSizeOptions={false}
                filterable={false}
                loading={appComparisonLoading}
              />
            </TitledCard>
            <TitledCard title="Incoming Application">
              <ReactTable
                data={incomingMembers}
                columns={incomingAppColumns}
                minRows={5}
                pageSize={5}
                showPageJump={false}
                showPageSizeOptions={false}
                filterable={false}
                loading={appComparisonLoading}
                getTrProps={(state, rowInfo, column) => {
                  if (rowInfo && rowInfo.original) {
                    const diffs =
                      rowInfo.original[CustomMemberFields.DifferingProperties];
                    const allNew = Object.keys(diffs).every((k) => diffs[k]);

                    return allNew
                      ? {
                          style: {
                            backgroundColor: '#fad97d',
                          },
                        }
                      : {};
                  }
                  return {};
                }}
              />
            </TitledCard>
            <TitledCard title="Resulting Application">
              <ReactTable
                data={resultingAppMembers}
                columns={resultingAppColumns}
                minRows={5}
                pageSize={5}
                showPageJump={false}
                showPageSizeOptions={false}
                filterable={false}
                loading={appComparisonLoading}
              />
            </TitledCard>
          </Fragment>
        )}
      </Modal.Content>
      <Modal.Actions>
        {step === STEPS.SelectExistingApplication ? (
          <Fragment>
            <Button
              primary={true}
              content="Next"
              onClick={() => {
                dispatch({
                  type: ActionTypes.SetStep,
                  payload: STEPS.MergeDuplicateApplication,
                });
                getExistingAppComparison(scannedAppId, selectedHHId);
              }}
              disabled={_.isNil(selectedHHId)}
            />
          </Fragment>
        ) : (
          <Fragment>
            <Button
              primary={true}
              content="Back"
              onClick={() =>
                dispatch({
                  type: ActionTypes.SetStep,
                  payload: STEPS.SelectExistingApplication,
                })
              }
              disabled={appComparisonLoading || updateLoading}
              className="pull-left"
            />
            <Button
              primary={true}
              content="Create New Application"
              onClick={() => {
                const errorMsg = getCreateOrEditErrorMsg(resultingApp);

                if (errorMsg) {
                  createToast(toastType.error, 'Error', errorMsg, true);
                  return;
                }

                setNotificationDialog({
                  ...NOTIFICATION_DIALOG_MESSAGES.CONFIRM_CREATE_NEW_APP,
                  functionsToRunOnOk: [
                    () => {
                      const queryParams = {
                        [QUERY_PARAMS.Ref]: PAGE_REFS.ReviewIncomingApplication,
                        [QUERY_PARAMS.IncomingAppId]: scannedAppId,
                      };
                      const queryString = qs.stringify(queryParams);

                      savePageState();
                      history.push(`manageapplication/0?${queryString}`);
                      startNewApplication(resultingApp);
                    },
                  ],
                });
              }}
              disabled={
                appComparisonLoading ||
                updateLoading ||
                disableStartNewApplicationBtn
              }
            />
            <Button
              primary={true}
              content="Continue"
              onClick={() => {
                const errorMsg = getCreateOrEditErrorMsg(resultingApp);

                if (errorMsg) {
                  createToast(toastType.error, 'Error', errorMsg, true);
                  return;
                }

                setNotificationDialog({
                  ...NOTIFICATION_DIALOG_MESSAGES.CONFIRM_UPDATE_EXISTING_APP,
                  functionsToRunOnOk: [
                    () => {
                      const queryParams = {
                        [QUERY_PARAMS.Ref]: PAGE_REFS.ReviewIncomingApplication,
                        [QUERY_PARAMS.IncomingAppId]: scannedAppId,
                      };
                      const queryString = qs.stringify(queryParams);

                      savePageState();
                      history.push(
                        `manageapplication/${selectedHHId}?${queryString}`
                      );
                      editApplication(resultingApp);
                    },
                  ],
                });
              }}
              disabled={appComparisonLoading || updateLoading}
            />
          </Fragment>
        )}
      </Modal.Actions>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  existingMembers: DuplicateAppsSelectors.getExistingAppHouseholdMembers(state),
  incomingMembers: DuplicateAppsSelectors.getIncomingAppHouseHoldMembers(state),
  existingApp: DuplicateAppsSelectors.getExistingApplication(state),
  appComparisonLoading: DuplicateAppsSelectors.getAppComparisonLoading(state),
  errors: DuplicateAppsSelectors.getErrors(state),
  duplicateStudentMatches: DuplicateAppsSelectors.getDuplicateStudentMatches(
    state
  ),
  incomingHOH: DuplicateAppsSelectors.getIncomingHOH(state),
  caseTypes: CommonStateSelectors.getCaseTypes(state),
  existingStudents: DuplicateAppsSelectors.getExistingAppStudents(state),
  savedModalState: RIAStateCacheSelectors.getSavedReviewDuplicatesModalState(
    state
  ),
});

const mapDispatchToProps = {
  setNotificationDialog,
  clearErrors: DuplicateAppsActions.clearErrors,
  startNewApplication: DuplicateAppsActions.startNewApplication,
  getExistingAppComparison: DuplicateAppsActions.getExistingAppComparison,
  editApplication: DuplicateAppsActions.editApplication,
  addPageState: PageStateCacheActions.addPageState,
  removePageState: PageStateCacheActions.removePageState,
};

const ConnectedComponent = connect(
  mapStateToProps,
  mapDispatchToProps
)(ReviewDuplicatesModal);

export { ConnectedComponent as ReviewDuplicatesModal };



// WEBPACK FOOTER //
// ./src/pages/reviewincomingapplications/components/ReviewDuplicatesModal.js